import React, { useEffect, useMemo, useState, useCallback } from 'react';
import * as Sift from '@sift/types';
import * as Types from 'pages/Pages/typings';
import { Prompt } from 'react-router';
import { Formik, Form } from 'formik';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import { LayoutObject } from 'pages/AttributesEdit/AttributesEdit';
import * as FormValues from 'types/FormValue';
import convertStringToArrayOfStrings from 'helpers/convertStringToArrayOfStrings';

import PageFormToolbar from 'components/Page/newComponents/PageFormToolbar';
import PageFormErrorMessage from 'components/Page/newComponents/PageFormErrorMessage';
import SaveButton from 'components/Page/SaveButton';
import CancelButton from 'components/Page/CancelButton';
import AttributeDataMapping from 'pages/AttributesEdit/components/AttributeDataMapping';
import JoinPage from './components/JoinPage';
import Terminology from 'pages/General/tabs/Settings/Terminology';
import PageMemberGrouping from './components/PageMemberGrouping';
import HandleRedirect from 'molecules/HandleRedirect';
import AttributeUsage from 'pages/AttributesEdit/components/AttributeUsage';
import { PERSON_ENTITY_TYPE_KEY } from 'helpers/useEntityTypes';
import { LayoutUpdateShape } from 'pages/Features/typings';

interface Props {
  attribute?: Sift.Field;
  combinedStatus: number;
  isPersonAttribute: boolean;
  userFields: FormValues.Fields;
  entityType: Types.EntityType;
  personEntityType: Types.EntityType;
  onUpdateLayout: (payload: LayoutObject[]) => void;
  onUpdateEntityType: (data: Types.UpdateEntity) => void;
  onUpdateField: (fieldId: string, editedAttribute: Types.UpdateField) => void;
}

function MembershipTab({
  attribute,
  combinedStatus,
  isPersonAttribute,
  userFields,
  entityType,
  personEntityType,
  onUpdateLayout,
  onUpdateEntityType,
  onUpdateField,
}: Props) {
  const personClientFieldObjectKey = _get(entityType, '_personClientFieldObjectKey');

  const field =
    personClientFieldObjectKey &&
    (_get(userFields, personClientFieldObjectKey) as Sift.Field | null);
  const fieldId = _get(field, 'id');
  const [formattedSelectOptions, setFormattedSelectOptions] = useState<string>('');

  const currentSelectOptions = _get(entityType, [
    '_settings',
    'relationshipMetadataAttribute',
    'selectOptions',
  ]);

  useEffect(() => {
    if (currentSelectOptions) {
      if (currentSelectOptions.length > 0) {
        setFormattedSelectOptions(currentSelectOptions.join(', '));
      } else {
        setFormattedSelectOptions('');
      }
    }
  }, [currentSelectOptions]);

  const valueReadOnly = useMemo(() => {
    return _get(field, 'valueReadOnly', false);
  }, [field]);

  const initialState = {
    settings: {
      ...entityType._settings,
      ...(entityType._settings.relationshipMetadataAttribute && {
        relationshipMetadataAttribute: {
          ...entityType._settings.relationshipMetadataAttribute,
          selectOptions: formattedSelectOptions,
        },
      }),
    },
    isSyncManaged: entityType._isSyncManaged,
    layouts: personEntityType._layouts,
    valueReadOnly: valueReadOnly,
  } as Types.EntityEditFormValues;

  const initialStateSettings = _get(initialState, 'settings');
  const initialStateFieldValue = _get(initialState, 'valueReadOnly');
  const initialStateLayouts = _get(initialState, 'layouts');

  const handleValidation = (values: Types.EntityEditFormValues) => {
    let errors = {} as Types.EntitySettingsFormErrors;

    // grouping/allowCreation/selectOptions
    const grouping = _get(values, 'settings.relationshipMetadataAttribute', {});
    if (
      _get(grouping, 'enabled') &&
      !_get(grouping, 'allowCreation') &&
      _get(grouping, 'selectOptions', '').length === 0
    ) {
      errors.selectOptions =
        'You must specify at least one preset group option if not allowing editors to create new groups.';
    }

    // member terminology
    const memberTerminolgy = _get(values, 'settings.memberTerminology', '');
    if (!memberTerminolgy.length) {
      errors.terminology = 'Member is a required field.';
    }

    return errors;
  };

  const handleUpdateLayout = useCallback(
    (layoutValues: LayoutUpdateShape | null) => {
      const layoutPayloadKeys = layoutValues
        ? Object.keys(layoutValues).filter(
            key => !_isEqual(layoutValues[key], _get(initialStateLayouts, key)),
          )
        : null;
      const layoutPayload = layoutPayloadKeys
        ? layoutPayloadKeys.map(key => ({
            entityField: PERSON_ENTITY_TYPE_KEY,
            layoutKey: key,
            children: _get(layoutValues, key, []),
          }))
        : null;
      if (layoutPayload && layoutPayload.length > 0) {
        onUpdateLayout(layoutPayload);
      }
    },
    [initialStateLayouts],
  );

  const handleUpdateEntityType = useCallback(
    (settingsValues: Types.EditSettings | null) => {
      const settingsChanged = !_isEqual(initialStateSettings, settingsValues);
      if (settingsChanged) {
        const selectOptionsAsArray = convertStringToArrayOfStrings(
          _get(settingsValues, ['relationshipMetadataAttribute', 'selectOptions']),
        );
        const settingsValuesWithOptionsConvertedToArray = {
          ...settingsValues,
          ...(settingsValues &&
            settingsValues.relationshipMetadataAttribute && {
              relationshipMetadataAttribute: {
                ...settingsValues.relationshipMetadataAttribute,
                selectOptions: settingsValues.relationshipMetadataAttribute.selectOptions
                  ? selectOptionsAsArray
                  : [],
              },
            }),
        } as Types.UpdateEntitySettings;

        onUpdateEntityType({ settings: settingsValuesWithOptionsConvertedToArray });
      }
    },
    [initialStateSettings],
  );

  const handleUpdateField = useCallback(
    (fieldValue: boolean) => {
      const fieldValueChanged = fieldValue !== initialStateFieldValue;
      fieldValueChanged && fieldId && onUpdateField(fieldId, { valueReadOnly: fieldValue });
    },
    [initialStateFieldValue, fieldId],
  );

  const handleSubmit = (values: Types.EntityEditFormValues) => {
    // conditional updates
    const layoutValues = _get(values, 'layouts', null);
    handleUpdateLayout(layoutValues);

    const settingsValues = _get(values, 'settings', null);
    handleUpdateEntityType(settingsValues);

    const fieldValue = _get(values, 'valueReadOnly');
    handleUpdateField(fieldValue);
  };

  return (
    <>
      {attribute && <AttributeDataMapping attribute={attribute} />}
      {attribute && (
        <Formik
          enableReinitialize
          initialValues={initialState}
          validate={handleValidation}
          onSubmit={handleSubmit}
        >
          {props => {
            const { values, setFieldValue, dirty, resetForm } = props;
            const errors = props.errors as Types.EntitySettingsFormErrors;

            return (
              <Form>
                <JoinPage values={values} onChange={setFieldValue} />
                <Terminology
                  defaultValue="Member"
                  errorMessage={errors}
                  subtitle="Change what words are used to refer to members of a page."
                  title="Terminology"
                  teamMemberUpdateKey="settings.memberTerminology"
                  value={{
                    teamMember: _get(values, 'settings.memberTerminology', ''),
                  }}
                  onChange={setFieldValue}
                />
                <PageMemberGrouping values={values.settings} onChange={setFieldValue} />
                {isPersonAttribute && (
                  <AttributeUsage
                    attribute={attribute}
                    fields={Object.values(userFields)}
                    values={values.layouts}
                    entityType={personEntityType}
                    status={combinedStatus}
                    typeKey={personEntityType.typeKey}
                    onChange={setFieldValue}
                  />
                )}
                <PageFormToolbar
                  dirty={dirty || !_isEmpty(errors)}
                  preContent={<PageFormErrorMessage errors={errors} />}
                >
                  <CancelButton
                    data-testid="membership-cancel"
                    onClick={resetForm}
                    disabled={!_isEmpty(errors)}
                  />
                  <SaveButton
                    label="Save"
                    onClick={() => {
                      props.handleSubmit();
                      resetForm({ values });
                    }}
                    disabled={!dirty || !_isEmpty(errors)}
                  />
                </PageFormToolbar>
                <Prompt
                  when={dirty || !_isEmpty(errors)}
                  message="Your work is not saved, or you have errors that need to be addressed! Are you sure you want to leave?"
                />
                <HandleRedirect />
              </Form>
            );
          }}
        </Formik>
      )}
    </>
  );
}

export default MembershipTab;
